/**
 * Human-readable label for a dd_schema_registry row.
 *
 * On single-level engines the schema name is unique enough on its own. On MS SQL
 * Server every dictionary has schemas called `dbo`, `Sales`, `HR`, so a list that
 * spans dictionaries shows several indistinguishable `dbo` entries.
 *
 * No migration is needed to fix that: getAllSchemas already joins and returns
 * instance_name, instance_type and environment alongside the schema row. This just
 * renders the pair.
 *
 * Deliberately NOT stored. dd_schema_registry has display_name and logical_name
 * columns, but both are read nowhere, and a denormalized label drifts out of sync
 * with schema_name after a rename. database_name stays the machine value used to
 * open a connection — a different job from this.
 */

// Rows reach the frontend either transformed (camelCase) or raw (snake_case).
const pick = (row, camel, snake) => (row && (row[camel] !== undefined ? row[camel] : row[snake])) || '';

export const schemaName = (row) => pick(row, 'schemaName', 'schema_name') || pick(row, 'name', 'name');
export const instanceName = (row) => pick(row, 'instanceName', 'instance_name');
export const databaseName = (row) => pick(row, 'databaseName', 'database_name');

/**
 * `Dictionary / schema` when the dictionary is known, otherwise just the schema.
 * Use wherever schemas from more than one dictionary appear in the same list.
 *
 * @param {Object} row  a dd_schema_registry row, transformed or raw
 * @return {string}
 */
export const schemaLabel = (row) => {
  const schema = schemaName(row);
  const instance = instanceName(row);
  if (!schema) return instance || '';
  return instance ? `${instance} / ${schema}` : schema;
};

export default { schemaLabel, schemaName, instanceName, databaseName };
